import { ACCOUNT_ROLES, ACCOUNT_ROLES_NEW } from '@consts';

import { isValueExist } from '@util';

const { ADMIN, NORMAL } = ACCOUNT_ROLES;
const {
    ADMIN: ADMIN_NEW,
    VIEW_AND_EDIT,
    CONNECTION_MANAGEMENT,
} = ACCOUNT_ROLES_NEW;

const BILLING_HISTORY_BTN_VISIBLE = 'BILLING_HISTORY_BTN_VISIBLE';
const BILLING_HISTORY_PAGE_VISIBLE = 'BILLING_HISTORY_PAGE_VISIBLE';
const BIZ_REG_VISIBLE = 'BIZ_REG_VISIBLE';
const CAMPAIGN_CREATE = 'CAMPAIGN_CREATE';
const CAMPAIGN_LAUNCH = 'CAMPAIGN_LAUNCH';
const CAMPAIGN_DELETE = 'CAMPAIGN_DELETE';

const CONNECTION_CREDENTIALS_MANAGE = 'CONNECTION_CREDENTIALS_MANAGE';
const CONNECTION_CREDENTIALS_DELETE = 'CONNECTION_CREDENTIALS_DELETE';
const CONNECTION_DATA_SOURCES_MANAGE = 'CONNECTION_DATA_SOURCES_MANAGE';
const CONNECTION_DATA_SOURCES_DELETE = 'CONNECTION_DATA_SOURCES_DELETE';

const TEAMSPACE_CREATE = 'TEAMSPACE_CREATE';
const TEAMSPACE_EDIT = 'TEAMSPACE_EDIT';
const TEAMSPACE_DELETE = 'TEAMSPACE_DELETE';

const SPEND_RULE_EDIT = 'MARGIN_MANAGEMENT_EDIT';

const MEMBER_INVITE = 'MEMBER_INVITE';

const ADMIN_ONLY = new Set([ADMIN, ADMIN_NEW]);
const ADMIN_NORMAL = new Set([ADMIN, NORMAL, ADMIN_NEW, VIEW_AND_EDIT]);
/**
 * connection 관리 액션의 legacy fallback — capability 레코드가 없는
 * 워크스페이스에서만 쓰인다.
 *
 * VIEW_AND_EDIT이 빠져 있지만 non-agency의 view&edit을 막지는 않는다.
 * `getRefinedRollByAgentUsage`(store/user.js)가 `meta.isAgency === false`인
 * 워크스페이스의 `['view_and_edit']`에 `connection_management`를 붙여서
 * role을 넘기기 때문 — 즉 아래 두 갈래가 된다.
 *   - non-agency: view&edit → connection 관리 O (role이 이미 보강됨)
 *   - agency:     view&edit → connection 관리 X (열람만)
 */
const ADMIN_NORMAL_CONNECTION_MANAGEMENT = new Set([
    ADMIN,
    NORMAL,
    ADMIN_NEW,
    CONNECTION_MANAGEMENT,
]);

/**
 * permission key → capability key.
 * 여기 매핑된 key만 capability로 판정하고, 나머지는 ROLE_PERMISSION_MAP으로
 * 떨어진다. Vue에 게터가 있는 key만 넣는다 — 새 게터를 추가할 때 같이 넣을 것.
 */
const PERMISSION_TYPE_TO_CAPABILITY_MAP = {
    [CONNECTION_CREDENTIALS_MANAGE]: 'connection.credentials.manage',
    [CONNECTION_CREDENTIALS_DELETE]: 'connection.credentials.delete',
    [CONNECTION_DATA_SOURCES_MANAGE]: 'connection.data_sources.manage',
    [CONNECTION_DATA_SOURCES_DELETE]: 'connection.data_sources.delete',

    // teamspaces.manage가 create와 edit을 함께 커버한다 (capability grain 1:1)
    [TEAMSPACE_CREATE]: 'teamspaces.manage',
    [TEAMSPACE_EDIT]: 'teamspaces.manage',
    [TEAMSPACE_DELETE]: 'teamspaces.delete',

    [SPEND_RULE_EDIT]: 'margin_management.edit',

    [MEMBER_INVITE]: 'members.invite',
};

const ROLE_PERMISSION_MAP = {
    [BILLING_HISTORY_BTN_VISIBLE]: ADMIN_ONLY,
    [BILLING_HISTORY_PAGE_VISIBLE]: ADMIN_ONLY,
    [BIZ_REG_VISIBLE]: ADMIN_NORMAL,
    [CAMPAIGN_CREATE]: ADMIN_NORMAL,
    [CAMPAIGN_LAUNCH]: ADMIN_NORMAL,
    [CAMPAIGN_DELETE]: ADMIN_ONLY,

    /**
     * fallback for capability based permission
     */
    [CONNECTION_CREDENTIALS_MANAGE]: ADMIN_NORMAL_CONNECTION_MANAGEMENT,
    [CONNECTION_CREDENTIALS_DELETE]: ADMIN_NORMAL_CONNECTION_MANAGEMENT,
    [CONNECTION_DATA_SOURCES_MANAGE]: ADMIN_NORMAL_CONNECTION_MANAGEMENT,
    [CONNECTION_DATA_SOURCES_DELETE]: ADMIN_NORMAL_CONNECTION_MANAGEMENT,

    [TEAMSPACE_CREATE]: ADMIN_NORMAL,
    [TEAMSPACE_EDIT]: ADMIN_NORMAL,
    [TEAMSPACE_DELETE]: ADMIN_NORMAL,

    [SPEND_RULE_EDIT]: ADMIN_NORMAL,

    [MEMBER_INVITE]: ADMIN_ONLY,
};

const permissionByRole = (key) =>
    R.compose((f) => f(key), R.prop('permission'), R.nthArg(1));

export default {
    namespaced: true,
    state: getDefaultState(),
    mutations: {},
    actions: {},
    getters: {
        /**
         * 권한 판정은 2층 구조다 (계약: BE 확정 2026-09-08).
         * React(adriel-fe-react `permissionByKeyAtom`)와 동일한 규칙이며,
         * 같은 사용자는 어느 화면에서든 같은 판정을 받아야 한다.
         *
         *   공통 가드 — share mode는 거부, backoffice admin은 통과
         *   1층 capability — capabilities 맵 기반. 관할 밖이면 role로 내려간다
         *   1층 role       — ROLE_PERMISSION_MAP 화이트리스트 기반
         *
         * 워크스페이스 admin을 공통 가드에서 단락시키지 않는 것이 핵심이다.
         * 단락시키면 capability 토글을 꺼도 통과해 버린다. admin은 모든
         * 화이트리스트에 포함돼 있으므로 role 층에서 그대로 통과한다.
         */
        permission:
            (_, _2, rootState, rootGetters) =>
            (key = '') => {
                const isShareMode = rootState['newDashboard/isShareMode'];
                const isAdmin = rootGetters['user/isAdmin'];

                if (isShareMode) {
                    return false;
                }

                if (isAdmin) {
                    return true;
                }

                const role = rootGetters['user/role'];

                // 1층 — capability 판정.
                // 매핑이 없는 액션이거나 capabilities 맵을 못 받은 워크스페이스는
                // capability 관할 밖이라 아래 role 판정으로 내려간다.
                const capabilityKey = PERMISSION_TYPE_TO_CAPABILITY_MAP[key];
                const capabilities = rootGetters['user/account']?.capabilities;

                if (capabilityKey && isValueExist(capabilities)) {
                    return capabilities[capabilityKey] === true;
                }

                // 1층 — role 판정. 화이트리스트가 없는 key는 허용이 기본값이다.
                const whiteList = ROLE_PERMISSION_MAP[key];

                if (!isValueExist(whiteList)) {
                    return true;
                }

                return role.some((r) => whiteList.has(r));
            },
        paymentVisible: R.T,
        paymentAddable: R.T,
        paymentSelectable: R.T,
        paymentDeletable: R.T,
        bizNoEditable: R.T,

        /**
         * manage by role
         */
        billingHistoryBtnVisible: permissionByRole(BILLING_HISTORY_BTN_VISIBLE),
        billingHistoryPageVisible: permissionByRole(
            BILLING_HISTORY_PAGE_VISIBLE
        ),
        bizRegVisible(...args) {
            return (
                permissionByRole(BIZ_REG_VISIBLE)(...args) &&
                R.converge(R.equals, [
                    R.path(['user/user', 'id']),
                    R.path(['user/account', 'owner_id']),
                ])(args[3])
            );
        },
        campaignCreatable: permissionByRole(CAMPAIGN_CREATE),
        campaignLaunchable: permissionByRole(CAMPAIGN_LAUNCH),
        campaignDeletable: permissionByRole(CAMPAIGN_DELETE),

        /**
         * manage by capability
         */
        connectionCredentialsManageable: permissionByRole(
            CONNECTION_CREDENTIALS_MANAGE
        ),
        connectionCredentialsDeletable: permissionByRole(
            CONNECTION_CREDENTIALS_DELETE
        ),
        connectionDataSourcesManageable: permissionByRole(
            CONNECTION_DATA_SOURCES_MANAGE
        ),
        connectionDataSourcesDeletable: permissionByRole(
            CONNECTION_DATA_SOURCES_DELETE
        ),

        teamspaceCreatable: permissionByRole(TEAMSPACE_CREATE),
        teamspaceEditable: permissionByRole(TEAMSPACE_EDIT),
        teamspaceDeletable: permissionByRole(TEAMSPACE_DELETE),

        spendRuleEditable: permissionByRole(SPEND_RULE_EDIT),

        memberInvitable: permissionByRole(MEMBER_INVITE),

        /**
         * Connection page 접근 판정. 전용 permission key 대신 connection
         * capability 4종의 OR로 본다 — 하나도 없으면 그 페이지에서 할 수 있는
         * 동작이 남지 않는다(PRD: capability가 모두 꺼져 있으면 접근 불가).
         *
         * LNB 노출(Navigation)과 view 진입 가드(views/Connection)가 같은 값을
         * 봐야 한다. 각자 판정하면 메뉴는 보이는데 들어가면 튕기는 상태가 난다.
         */
        connectionManageable: (_, getters) =>
            Boolean(
                getters.connectionCredentialsManageable ||
                    getters.connectionCredentialsDeletable ||
                    getters.connectionDataSourcesManageable ||
                    getters.connectionDataSourcesDeletable
            ),
    },
};

function getDefaultState() {
    return {};
}
